import {
  ClientDocSubmissionNextStatusEnum,
  ClientDocSubmissionNextStatusEnumKeys,
  ClientDocSubmissionStatusEnum,
  ClientDocSubmissionStatusEnumKeys,
} from "../ekyc.shared.enums";

export interface IClientDocSubmissionDetailsRequest {
  remoteId: string;
  username: string;
  previousDuplicates: string[];
}

export interface IClientImagesRequest {
  submissionRequestId: string;
  imageType: ImageType;
}

export interface IClientAdditionalImagesRequest {
  submissionRequestId: string;
  imageType: ImageType;
}

export enum EFieldStatus {
  REVIEW = "REVIEW",
  ACCEPTED = "ACCEPTED",
  EDITED = "EDITED",
}

export interface IField {
  value: string | number;
  status: EFieldStatus;
}
export interface IClientDocSubmissionDetailsResponse {
  // TBD: Some properties of this interface are outdated,
  // we're still waiting for the market to inform if records of previous versions will implement this interface
  id: string;
  requestId: string;
  firstName: IField;
  lastName: IField;
  dateOfBirth: IField;
  placeOfBirth?: IField;
  gender?: IField;
  documentType: string;
  documentNumber: IField;
  nationality: IField;
  occupation?: IField;
  income?: IField;
  riskRating?: IField;
  idCardIssuingBy?: IField;
  idCardIssuingDate?: IField;
  idCardIssuingPlace?: IField;
  commune?: IField;
  street?: IField;
  province?: IField;
  town?: IField;
  msisdn: string;
  creationDate: number;
  updateStatusDate?: number;
  status: ClientDocSubmissionStatusEnum;
  comment?: string;
  expirationDate?: number;
  language: string;
  numberOfPhoneNumberDuplications: number;
  numberOfIdNumberDuplications: number;
  signature?: string;
  frontPhoto?: string;
  backPhoto?: string;
  selfie?: string;
  visa?: string;
  lastGetUser?: string;
  lastGetTime?: number;
  penultGetUser?: string;
  penultGetTime?: number;
  registrationHistories?: IStatusHistoryEntry[];
  changeHistories?: IStatusTaskHistoryEntry[];
  riskRatingHistory?: IRiskRatingHistoryEntry[];
  operationType: string;
  duplicateFaces?: string[] | null;
  duplicateDocuments?: string[] | null;
  g2ErrorComment?: string;
  agent: {
    agentName: string;
    middleName: string;
    lastName: string;
    agentOperatorId: string;
    agentShortCode: number;
  };
  g2Status?: string;
  lastOperator?: {
    name: string;
    lastUpdated: number;
  };
  penultimateOperator?: {
    name: string;
    lastUpdated: number;
  };
  statusCode?: string;
  whiteListedUser?: boolean;
}

export interface IClientDocSubmissionChangeRequest {
  status: ClientDocSubmissionNextStatusEnum;
  comment?: string;
  customComment?: string;
}

export interface IClientFormSubmissionChangeRequest {
  firstName?: {
    value: string | number;
    status: string;
  };
  lastName?: {
    value: string | number;
    status: string;
  };
  dateOfBirth?: {
    value: string | number;
    status: string;
  };
  placeOfBirth?: {
    value: string | number;
    status: string;
  };
  idNumber?: {
    value: string | number;
    status: string;
  };
  nationality?: {
    value: string | number;
    status: string;
  };
  occupation?: {
    value: string | number;
    status: string;
  };
  income?: {
    value: string | number;
    status: string;
  };
  riskRating?: {
    value: string | number;
    status: string;
  };
  idCardIssuingBy?: {
    value: string | number;
    status: string;
  };
  idCardIssuingDate?: {
    value: string | number;
    status: string;
  };
  idCardIssuingPlace?: {
    value: string | number;
    status: string;
  };
  gender?: {
    value: string | number;
    status: string;
  };

  street?: {
    value: string | number;
    status: string;
  };
  province?: {
    value: string | number;
    status: string;
  };
  town?: {
    value: string | number;
    status: string;
  };
  commune?: {
    value: string | number;
    status: string;
  };
  documentType?: string | number;
  operator?: string;
  comment?: string;
}

export interface IStatusHistoryEntry {
  operator?: string;
  status?: ClientDocSubmissionStatusEnum;
  statusDate?: number;
}

export interface IClientDocSubmissionDetailsImagesResponse {
  content: string;
}

export interface IPhotoInfo {
  context: string;
  extension: string;
  url: string;
}

export enum ImageType {
  FRONT = "FRONT",
  BACK = "BACK",
  SELFIE = "SELFIE",
  ADDITIONAL_DOCUMENTS = "ADDITIONAL_DOCUMENTS",
}

export interface IDocValidationDetailsPageProps {
  remoteId: string;
}

export interface ICustomReasonCharactersLimit {
  max: number;
  min: number;
}

export interface IDocStatusItemProps {
  status: ClientDocSubmissionNextStatusEnumKeys | ClientDocSubmissionStatusEnumKeys;
}

export interface IDisableOptionProps {
  currentOption: ClientDocSubmissionNextStatusEnumKeys;
  optionToBeDisabled: ClientDocSubmissionNextStatusEnumKeys;
  currentStatus: ClientDocSubmissionStatusEnumKeys;
  forbiddenStatuses: ClientDocSubmissionStatusEnumKeys[];
}

export interface IDocValidationDetailsReducerInterface {
  remoteId: string;
  requestId: string;
  fullName: string;
  firstName?: IField;
  lastName?: IField;
  dateOfBirth: IField;
  placeOfBirth?: IField;
  gender?: IField;
  documentType: string;
  idNumber: IField;
  nationality: IField;
  occupation?: IField;
  riskRating?: IField;
  income?: IField;
  idCardIssuingDate?: IField;
  idCardIssuingPlace?: IField;
  idCardIssuingBy?: IField;
  addressCommune?: IField;
  addressStreet?: IField;
  addressProvince?: IField;
  addressTown?: IField;
  phoneNumber: string;
  requestTime: number;
  lastStatusTime?: number;
  status: ClientDocSubmissionStatusEnumKeys;
  reason?: string;
  expirationDate?: number;
  userLanguage: string;
  signatureUrl?: string;
  numberOfIdNumberDuplications: number;
  numberOfPhoneNumberDuplications: number;
  lastSeenBy?: string;
  lastSeenTime?: number;
  penultSeenBy?: string;
  penultSeenTime?: number;
  statusHistory: IClientDocStatusHistoryEntry[];
  changeHistories: IClientDocTaskHistoryEntry[];
  riskRatingHistory: IRiskRatingHistoryEntry[];
  operationType: string;
  duplicateFaces?: string[] | null;
  duplicateDocuments?: string[] | null;
  g2Error?: string;
  agent: {
    firstName: string;
    middleName: string;
    lastName: string;
    operatorId: string;
    shortcode: number | string;
  };
  g2Status?: string;
  lastOperator?: {
    name: string;
    lastUpdated: number;
  };
  penultimateOperator?: {
    name: string;
    lastUpdated: number;
  };
  statusCode?: string;
  whiteListedUser?: boolean;
}

export interface IDocValidationDetailsImagesReducerInterface {
  context: string;
  extension: string;
  url: string;
}

export interface IDocValidationAdditionalImagesReducerInterface {
  context: string;
  extension: string;
  url: string;
  content?: string;
}

export interface IClientDocStatusHistoryEntry {
  username?: string;
  status: ClientDocSubmissionStatusEnumKeys;
  statusDate: number;
}

export interface IRiskRatingHistoryEntry {
  operator?: string;
  riskRating: string;
  updatedAt: number;
}

export interface IClientDocTaskHistoryEntry {
  field?: string;
  oldValue?: string;
  newValue?: string;
  status: string;
  statusDate: number;
  statusReason: string;
  comment: string;
  approver?: string;
  operator?: string;
  dateCreated: string;
  dateUpdated: string;
  changes?: { field: string; oldValue: string; newValue: string }[];
}

export interface IStatusTaskHistoryEntry {
  status: string;
  statusDate: number;
  statusReason: string;
  comment: string;
  approver?: string;
  operator?: string;
  dateCreated: string;
  dateUpdated: string;
  changeHistoryData?: { field: string; oldValue: string; newValue: string }[];
}

export interface IClientFormSubmissionChangeRequest {
  firstName?: {
    value: string | number;
    status: string;
  };
  lastName?: {
    value: string | number;
    status: string;
  };
  dateOfBirth?: {
    value: string | number;
    status: string;
  };
  placeOfBirth?: {
    value: string | number;
    status: string;
  };
  idNumber?: {
    value: string | number;
    status: string;
  };
  nationality?: {
    value: string | number;
    status: string;
  };
  gender?: {
    value: string | number;
    status: string;
  };
  occupation?: {
    value: string | number;
    status: string;
  };
  income?: {
    value: string | number;
    status: string;
  };
  riskRating?: {
    value: string | number;
    status: string;
  };
  idCardIssuingDate?: {
    value: string | number;
    status: string;
  };
  idCardIssuingPlace?: {
    value: string | number;
    status: string;
  };
  idCardIssuingBy?: {
    value: string | number;
    status: string;
  };
  street?: {
    value: string | number;
    status: string;
  };
  province?: {
    value: string | number;
    status: string;
  };
  town?: {
    value: string | number;
    status: string;
  };
  commune?: {
    value: string | number;
    status: string;
  };
  documentType?: string | number;

  operator?: string;
  comment?: string;
}
